import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowLeft,
  CreditCard,
  Smartphone,
  Copy,
  Check,
  Loader2,
  XCircle,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OutstandingPaymentPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);
  
  // Payment flow states
  const [paymentStep, setPaymentStep] = useState("details"); // details, paynow, confirming, success
  const [payNowData, setPayNowData] = useState(null);
  const [accountHolderName, setAccountHolderName] = useState("");
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPaymentDetails();
  }, [token]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/outstanding-payment/${token}`);
      setPayment(response.data);
      
      // If already paid or waived, show appropriate status
      if (response.data.status === "paid") {
        setPaymentStep("success");
      }
    } catch (err) {
      console.error("Error fetching payment:", err);
      setError(err.response?.data?.detail || "Payment not found or link is invalid");
    } finally {
      setLoading(false);
    }
  };

  const initiatePayNow = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/payments/checkout/outstanding-payment`, {
        payment_token: token,
        payment_method: "paynow",
        origin_url: window.location.origin
      });
      
      setPayNowData(response.data);
      setPaymentStep("paynow");
    } catch (err) {
      console.error("Error initiating payment:", err);
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  // Feb 2026 — card payment path. Mirrors the adhoc-booking + registration
  // checkout flow: backend creates a Stripe checkout session, returns a
  // hosted URL, and we redirect. On success the user lands on
  // /academy/registration-success?type=outstanding which polls
  // /payments/checkout/status/{session_id} until Stripe says "paid", at
  // which point the backend webhook + status-poll handler closes the
  // outstanding payment record.
  const initiateCardPayment = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/payments/checkout/outstanding-payment`, {
        payment_token: token,
        payment_method: "card",
        origin_url: window.location.origin,
      });
      const url = response.data?.url;
      if (!url) {
        throw new Error("No checkout URL returned");
      }
      toast.success(`Reference ${response.data.payment_reference} — redirecting to secure checkout…`);
      window.location.href = url;
    } catch (err) {
      console.error("Error initiating card payment:", err);
      toast.error(err.response?.data?.detail || "Failed to start card payment");
      setLoading(false);
    }
  };

  const confirmPayNowPayment = async () => {
    if (!payNowData) return;
    
    try {
      setConfirmingPayment(true);
      await axios.post(`${API}/payments/outstanding-payment/paynow/confirm`, {
        transaction_id: payNowData.transaction_id,
        payment_reference: payNowData.payment_reference,
        account_holder_name: accountHolderName
      });
      
      setPaymentStep("confirming");
      toast.success("Payment submitted for verification!");
    } catch (err) {
      console.error("Error confirming payment:", err);
      toast.error(err.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setConfirmingPayment(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const getReasonText = (reason) => {
    const reasons = {
      "section_membership_rejected": "Your section membership application was not approved",
      "membership_downgrade": "Your membership status has changed",
      "fee_adjustment": "There was a fee adjustment"
    };
    return reasons[reason] || "Fee adjustment required";
  };

  if (loading && !payment) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Payment Not Found</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment already completed
  if (payment?.status === "paid" || paymentStep === "success") {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Payment Complete</h2>
            <p className="text-slate-400 mb-2">Reference: <span className="font-mono font-bold">{payment?.payment_reference}</span></p>
            <p className="text-slate-400 mb-6">Thank you for your payment!</p>
            <Link to="/">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment waived
  if (payment?.status === "waived") {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Payment Waived</h2>
            <p className="text-slate-400 mb-6">This payment has been waived by the administrator. No payment is required.</p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirming state
  if (paymentStep === "confirming") {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Payment Submitted</h2>
            <p className="text-slate-400 mb-4">
              Your payment is being verified by our team. This usually takes 1-2 business days.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-400">
                Reference: <span className="font-mono font-bold">{payNowData?.payment_reference}</span>
              </p>
            </div>
            <p className="text-sm text-slate-400 mb-6">
              You will receive an email confirmation once your payment is verified.
            </p>
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PayNow payment step
  if (paymentStep === "paynow" && payNowData) {
    // Tag 62/05 (additional reference) and the inner routing field MUST
    // both be the fixed Wise account identifier "A21882030QR" — that is
    // the proxy value Wise uses to route inbound PayNow transfers into
    // the academy's account. Encoding the per-invoice SCC-XXX reference
    // here causes Wise to reject the payment and return funds to the
    // sender after 1 day. The SCC reference is shown to the parent as
    // display text below, never inside the encoded QR.
    const WISE_PROXY = 'A21882030QR';
    const qrData = `00020101021126380009SG.PAYNOW010120211${payNowData.paynow_uen}0301103129999${WISE_PROXY}530370254${payNowData.amount.toFixed(2).padStart(10, '0')}5802SG5913${payNowData.paynow_name}6009Singapore62240520${WISE_PROXY}6304`;
    
    return (
      <div className="min-h-screen bg-[#0a0f1a] py-8 px-4">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => setPaymentStep("details")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Card>
            <CardHeader className="text-center border-b bg-purple-500/10">
              <CardTitle className="flex items-center justify-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-400" />
                Pay with PayNow
              </CardTitle>
              <CardDescription>Scan the QR code with your banking app</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Amount */}
              <div className="text-center">
                <p className="text-sm text-slate-400">Amount Due</p>
                <p className="text-3xl font-bold text-purple-400">${payNowData.amount.toFixed(2)} SGD</p>
              </div>
              
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-slate-900/50 p-4 rounded-lg border-2 border-purple-500/30">
                  <QRCodeSVG value={qrData} size={200} />
                </div>
              </div>
              
              {/* UEN Details */}
              <div className="bg-[#0a0f1a] rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">UEN</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold">{payNowData.paynow_uen}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(payNowData.paynow_uen)}
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Name</span>
                  <span className="font-medium">{payNowData.paynow_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Reference</span>
                  <span className="font-mono text-sm">{payNowData.payment_reference}</span>
                </div>
              </div>
              
              {/* Important Note */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-sm text-yellow-400">
                  <strong>Important:</strong> Please include the reference number <span className="font-mono">{payNowData.payment_reference}</span> in your transfer.
                </p>
              </div>
              
              {/* Account Holder Name */}
              <div className="space-y-2">
                <Label htmlFor="accountHolder">Your Bank Account Holder Name</Label>
                <Input
                  id="accountHolder"
                  placeholder="Enter the name on your bank account"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                />
                <p className="text-xs text-slate-400">This helps us verify your payment faster</p>
              </div>
              
              {/* Confirm Button */}
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={confirmPayNowPayment}
                disabled={confirmingPayment}
              >
                {confirmingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  "I've Made the Payment"
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Payment details step (default)
  return (
    <div className="min-h-screen bg-[#0a0f1a] py-8 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>
        </Link>
        
        <Card className="overflow-hidden">
          <CardHeader className="text-center border-b bg-gradient-to-r from-red-500 to-red-600 text-white">
            <CardTitle className="flex items-center justify-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Outstanding Payment Required
            </CardTitle>
            <CardDescription className="text-red-100">
              Football And More Limited
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Reference */}
            <div className="text-center bg-red-500/10 rounded-lg p-4">
              <p className="text-sm text-slate-400 mb-1">Payment Reference</p>
              <p className="text-xl font-mono font-bold text-red-400">{payment.payment_reference}</p>
            </div>
            
            {/* Amount */}
            <div className="text-center">
              <p className="text-sm text-slate-400">Amount Due</p>
              <p className="text-4xl font-bold text-red-400">${payment.difference_amount.toFixed(2)}</p>
              <p className="text-sm text-slate-400">SGD</p>
            </div>
            
            {/* Details */}
            <div className="bg-[#0a0f1a] rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Parent Name</span>
                <span className="font-medium">{payment.parent_name}</span>
              </div>
              {payment.term_name && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Term</span>
                  <span className="font-medium">{payment.term_name}</span>
                </div>
              )}
              {payment.children_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Children</span>
                  <span className="font-medium">{payment.children_count}</span>
                </div>
              )}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Original Amount Paid</span>
                  <span>${payment.original_amount_paid.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Correct Amount</span>
                  <span>${payment.correct_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-red-400 mt-2">
                  <span>Difference Due</span>
                  <span>${payment.difference_amount.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Reason */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-sm text-yellow-400">
                <strong>Reason:</strong> {getReasonText(payment.reason)}
              </p>
            </div>
            
            {/* Payment Buttons — Feb 2026: parents can choose card OR PayNow.
                Previously this page only offered PayNow which trapped parents
                like Ben Olbourne who wanted to pay by card. */}
            <div className="space-y-3">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                onClick={initiateCardPayment}
                disabled={loading}
                data-testid="outstanding-pay-card-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${payment.difference_amount.toFixed(2)} by Card
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Visa / Mastercard / AMEX via Stripe — instant confirmation
              </p>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-700"></span>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#0a0f1a] px-2 text-slate-500">or</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full border-slate-600 text-slate-200 hover:bg-slate-800 text-lg py-6"
                onClick={initiatePayNow}
                disabled={loading}
                data-testid="outstanding-pay-paynow-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Smartphone className="w-5 h-5 mr-2" />
                    Pay ${payment.difference_amount.toFixed(2)} by PayNow QR
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Singapore bank app (1–2 business days to verify)
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Contact Support */}
        <p className="text-center text-sm text-slate-400 mt-6">
          Questions? Contact us at{" "}
          <a href="mailto:admin@sccfootball.com" className="text-blue-400 hover:underline">
            admin@sccfootball.com
          </a>
        </p>
      </div>
    </div>
  );
}
